import { ru } from './ru';
import {
  CUSTOM_BACKGROUND_PREFIX,
  getBackgroundOptions,
  isCustomBackgroundId,
  setCustomOptions,
} from './backgrounds';
import type { BackgroundOption } from './backgrounds';
import { ImageResolutionError, processImageFile } from './custom-background-image';

/** The picker offers at most this many uploaded backgrounds. */
export const MAX_CUSTOM_BACKGROUNDS = 10;
/** Uploads above this size are rejected before any decoding happens. */
export const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;

export type CustomBackgroundRecord = {
  id: string;
  name?: string;
  createdAt: number;
  full: Blob;
  thumb: Blob;
};

export type AddCustomBackgroundResult =
  | { ok: true; id: string; persisted: boolean }
  | {
      ok: false;
      reason: 'not-image' | 'too-large' | 'too-large-resolution' | 'too-many' | 'process-failed';
    };

export type CustomBackgroundsBackend = {
  list(): Promise<CustomBackgroundRecord[]>;
  put(record: CustomBackgroundRecord): Promise<void>;
  delete(id: string): Promise<void>;
};

// --- Validation and naming (pure, unit-tested) -----------------------------

/** Rejects non-images and oversized files before any decoding starts. */
export function validateImageUpload(file: {
  type: string;
  size: number;
}): 'not-image' | 'too-large' | undefined {
  if (!file.type.startsWith('image/')) {
    return 'not-image';
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    return 'too-large';
  }
  return undefined;
}

/** Tile label: the file name without its extension, or a generic one. */
export function labelForRecord(record: { name?: string }): string {
  const name = record.name?.replace(/\.[^.]+$/, '').trim();
  return name || ru.backgroundCustom;
}

// --- IndexedDB -------------------------------------------------------------

const DB_NAME = 'octor-backgrounds';
const DB_VERSION = 1;
const STORE_NAME = 'custom';

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is unavailable'));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) {
        request.result.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB open failed'));
  });
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });
}

/**
 * Waits for a write to be committed: a successful request does not rule out an
 * abort at commit time (quota, private mode), which would silently lose the
 * change on the next reload.
 */
function transactionCommitted(transaction: IDBTransaction, request: IDBRequest): Promise<void> {
  return new Promise((resolve, reject) => {
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
    transaction.oncomplete = () => resolve();
    transaction.onerror = () =>
      reject(transaction.error ?? new Error('IndexedDB transaction failed'));
    transaction.onabort = () =>
      reject(transaction.error ?? new Error('IndexedDB transaction aborted'));
  });
}

const indexedDbBackend: CustomBackgroundsBackend = {
  async list() {
    const db = await openDatabase();
    try {
      const store = db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME);
      return await requestToPromise(store.getAll() as IDBRequest<CustomBackgroundRecord[]>);
    } finally {
      db.close();
    }
  },
  async put(record) {
    const db = await openDatabase();
    try {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const request = transaction.objectStore(STORE_NAME).put(record);
      await transactionCommitted(transaction, request);
    } finally {
      db.close();
    }
  },
  async delete(id) {
    const db = await openDatabase();
    try {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const request = transaction.objectStore(STORE_NAME).delete(id);
      await transactionCommitted(transaction, request);
    } finally {
      db.close();
    }
  },
};

// Tests inject a fake backend instead of polyfilling IndexedDB.
let backendOverride: CustomBackgroundsBackend | undefined;

function backend(): CustomBackgroundsBackend {
  return backendOverride ?? indexedDbBackend;
}

export function setCustomBackgroundsBackendForTests(backend?: CustomBackgroundsBackend) {
  backendOverride = backend;
}

// --- Session state ---------------------------------------------------------

// Records stay in memory so blob URLs can be rebuilt after a back-forward
// cache restore (see the pageshow listener below).
const loadedRecords = new Map<string, CustomBackgroundRecord>();
// Backgrounds that could not reach IndexedDB (quota, private mode) but stay
// usable until the page is closed.
const sessionOnlyIds = new Set<string>();
// IndexedDB is broken for the whole session (private mode, blocked storage).
let storageUnavailable = false;

/** The uploaded part of the picker list, without the static options. */
function currentCustomOptions(): BackgroundOption[] {
  return getBackgroundOptions().filter((option) => isCustomBackgroundId(option.id));
}

let readyPromise: Promise<void> | undefined;

/** Loads records once and merges them into the picker's option list. */
export function customBackgroundsReady(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }
  readyPromise ??= loadAll();
  return readyPromise;
}

async function loadAll() {
  let records: CustomBackgroundRecord[];
  try {
    records = await backend().list();
  } catch (error) {
    console.error('Failed to load custom backgrounds', error);
    storageUnavailable = true;
    setCustomOptions([]);
    return;
  }
  setCustomOptions(
    records
      .filter(
        (record) =>
          record &&
          typeof record.id === 'string' &&
          // A corrupted record must not occupy a static id ("desk", "none"):
          // such a tile would be shadowed by the static options and stick
          // around unselectable and undeletable.
          isCustomBackgroundId(record.id) &&
          record.full instanceof Blob &&
          record.thumb instanceof Blob,
      )
      .sort((a, b) => a.createdAt - b.createdAt)
      .map((record) => {
        loadedRecords.set(record.id, record);
        return recordToOption(record);
      }),
  );
}

export function recordToOption(
  record: CustomBackgroundRecord,
  createObjectUrl: (blob: Blob) => string = (blob) => URL.createObjectURL(blob),
): BackgroundOption {
  return {
    id: record.id,
    kind: 'image',
    label: labelForRecord(record),
    src: createObjectUrl(record.full),
    thumb: createObjectUrl(record.thumb),
  };
}

function generateId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

// Adds mutate the shared option list, so they run one at a time: concurrent
// uploads from two pickers would otherwise both pass the limit check and push
// the list past its cap.
let addQueue: Promise<unknown> = Promise.resolve();

/**
 * Validates, resizes and adds an upload. The new background is applied to the
 * picker list right away; if IndexedDB refuses to store it (quota, private
 * mode) it stays usable for this session only.
 */
export function addCustomBackground(file: File): Promise<AddCustomBackgroundResult> {
  const run = addQueue.then(() => addCustomBackgroundQueued(file));
  addQueue = run.catch(() => undefined);
  return run;
}

async function addCustomBackgroundQueued(file: File): Promise<AddCustomBackgroundResult> {
  const invalid = validateImageUpload(file);
  if (invalid) {
    return { ok: false, reason: invalid };
  }
  await customBackgroundsReady();
  // Serialized by addQueue, so the count cannot grow under us: the only
  // mutation that can run alongside, removal, only shrinks the list.
  if (currentCustomOptions().length >= MAX_CUSTOM_BACKGROUNDS) {
    return { ok: false, reason: 'too-many' };
  }
  let image;
  try {
    image = await processImageFile(file);
  } catch (error) {
    if (error instanceof ImageResolutionError) {
      return { ok: false, reason: 'too-large-resolution' };
    }
    console.error('Failed to process custom background image', error);
    return { ok: false, reason: 'process-failed' };
  }
  const record: CustomBackgroundRecord = {
    id: `${CUSTOM_BACKGROUND_PREFIX}${generateId()}`,
    name: file.name || undefined,
    createdAt: Date.now(),
    full: image.full,
    thumb: image.thumb,
  };
  setCustomOptions([...currentCustomOptions(), recordToOption(record)]);
  loadedRecords.set(record.id, record);
  if (storageUnavailable) {
    sessionOnlyIds.add(record.id);
    return { ok: true, id: record.id, persisted: false };
  }
  try {
    await backend().put(record);
    return { ok: true, id: record.id, persisted: true };
  } catch (error) {
    console.error('Failed to save custom background', error);
    sessionOnlyIds.add(record.id);
    return { ok: true, id: record.id, persisted: false };
  }
}

/** Removes an upload from IndexedDB and the picker; false when it must stay. */
export async function removeCustomBackground(id: string): Promise<boolean> {
  const option = getBackgroundOptions().find((option) => option.id === id);
  if (!isCustomBackgroundId(id) || option?.kind !== 'image') {
    return false;
  }
  if (!sessionOnlyIds.has(id) && !storageUnavailable) {
    try {
      await backend().delete(id);
    } catch (error) {
      console.error('Failed to delete custom background', error);
      return false;
    }
  }
  setCustomOptions(currentCustomOptions().filter((option) => option.id !== id));
  loadedRecords.delete(id);
  sessionOnlyIds.delete(id);
  URL.revokeObjectURL(option.src);
  URL.revokeObjectURL(option.thumb);
  return true;
}

// Tests start from a clean module state.
export function resetCustomBackgroundsForTests() {
  backendOverride = undefined;
  loadedRecords.clear();
  sessionOnlyIds.clear();
  storageUnavailable = false;
  readyPromise = undefined;
  addQueue = Promise.resolve();
  setCustomOptions([]);
}

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    getBackgroundOptions().forEach((option) => {
      if (option.kind === 'image' && isCustomBackgroundId(option.id)) {
        URL.revokeObjectURL(option.src);
        URL.revokeObjectURL(option.thumb);
      }
    });
  });
  window.addEventListener('pageshow', (event) => {
    // Restored from the back-forward cache: the document survived but the blob
    // URLs were revoked, so they are minted again from the in-memory records.
    if (!(event as PageTransitionEvent).persisted || loadedRecords.size === 0) {
      return;
    }
    setCustomOptions([...loadedRecords.values()].map((record) => recordToOption(record)));
  });
}
